const createCase = ({
  id,
  suite,
  title,
  difficulty,
  type,
  feature,
  module,
  goal,
  precondition,
  problemStatement,
  executionSteps,
  expectedResults,
  locatorHints,
  seleniumHint,
  playwrightHint,
}) => ({
  id,
  suite,
  title,
  difficulty,
  type,
  feature,
  module,
  goal,
  precondition,
  problemStatement,
  executionSteps,
  expectedResults,
  locatorHints,
  seleniumHint,
  playwrightHint,
});

export const automationExerciseDetailedCases = [
  createCase({
    id: "ae-01",
    suite: "Automation Exercise Core",
    title: "Test Case 1: Register User",
    difficulty: "Intermediate",
    type: "UI E2E",
    feature: "Signup and account creation",
    module: "Authentication",
    goal: "Validate complete registration, post-login visibility, and account cleanup flow.",
    precondition: "Use a fresh email address that does not already exist in the system.",
    problemStatement: "This case verifies the full happy-path user onboarding journey from home page to account deletion.",
    executionSteps: [
      "Launch browser.",
      "Navigate to the application home page URL.",
      "Verify that the home page is visible successfully.",
      "Click on the 'Signup / Login' button.",
      "Verify that the 'New User Signup!' section is visible.",
      "Enter name and email address in the signup form.",
      "Click the 'Signup' button.",
      "Verify that 'ENTER ACCOUNT INFORMATION' is visible.",
      "Fill account information such as title, name, email, password, and date of birth.",
      "Select the newsletter subscription checkbox.",
      "Select the special offers checkbox.",
      "Fill address information including first name, last name, company, address, country, state, city, zipcode, and mobile number.",
      "Click the 'Create Account' button.",
      "Verify that 'ACCOUNT CREATED!' is visible.",
      "Click the 'Continue' button.",
      "Verify that 'Logged in as username' is visible.",
      "Click the 'Delete Account' button.",
      "Verify that 'ACCOUNT DELETED!' is visible and click 'Continue' button.",
    ],
    expectedResults: [
      "Account creation page should open after signup submission.",
      "User should be logged in successfully after clicking Continue.",
      "Delete account should remove the created user and show deleted confirmation.",
    ],
    locatorHints: ["signup-login nav action", "signup name input", "signup email input", "create account button", "delete account action"],
    seleniumHint: "Break this into page objects for Home, Signup/Login, Account Information, and Logged-in Header areas.",
    playwrightHint: "Use role-based selectors for headings, buttons, and visible logged-in state to avoid brittle CSS selectors.",
  }),
  createCase({
    id: "ae-02",
    suite: "Automation Exercise Core",
    title: "Test Case 2: Login User with correct email and password",
    difficulty: "Beginner",
    type: "UI Functional",
    feature: "Login with valid credentials",
    module: "Authentication",
    goal: "Confirm valid user can log in and then delete account successfully.",
    precondition: "A valid registered user must already exist in the application.",
    problemStatement: "This case validates positive login and correct post-login user identification.",
    executionSteps: [
      "Launch browser.",
      "Navigate to the application home page URL.",
      "Verify that the home page is visible successfully.",
      "Click on the 'Signup / Login' button.",
      "Verify that 'Login to your account' is visible.",
      "Enter correct email address and password.",
      "Click the 'Login' button.",
      "Verify that 'Logged in as username' is visible.",
      "Click the 'Delete Account' button.",
      "Verify that 'ACCOUNT DELETED!' is visible.",
    ],
    expectedResults: [
      "Valid credentials should take the user into authenticated state.",
      "Logged-in username should be visible in the header.",
      "Delete flow should remove the account successfully.",
    ],
    locatorHints: ["login email input", "login password input", "login submit button", "logged-in username label"],
    seleniumHint: "Assert both route stability and logged-in username text after login submission.",
    playwrightHint: "Use locator assertions for login heading before submit and logged-in username after submit.",
  }),
  createCase({
    id: "ae-03",
    suite: "Automation Exercise Core",
    title: "Test Case 3: Login User with incorrect email and password",
    difficulty: "Beginner",
    type: "Negative UI",
    feature: "Login validation",
    module: "Authentication",
    goal: "Verify invalid credentials return visible error without authenticating the user.",
    precondition: "The user stays on public state before running this test.",
    problemStatement: "This negative path ensures wrong credentials do not grant access and correct error messaging is shown.",
    executionSteps: [
      "Launch browser.",
      "Navigate to the application home page URL.",
      "Verify that the home page is visible successfully.",
      "Click on the 'Signup / Login' button.",
      "Verify that 'Login to your account' is visible.",
      "Enter incorrect email address and password.",
      "Click the 'Login' button.",
      "Verify that the error message 'Your email or password is incorrect!' is visible.",
    ],
    expectedResults: [
      "User should remain on login context after failed login.",
      "Incorrect credentials message should be displayed clearly.",
      "No authenticated username should appear in header.",
    ],
    locatorHints: ["login form", "error alert", "header auth state"],
    seleniumHint: "Assert error text and absence of logged-in header in the same test.",
    playwrightHint: "Use expect on error banner visibility and ensure no dashboard or auth-only element appears.",
  }),
  createCase({
    id: "ae-04",
    suite: "Automation Exercise Core",
    title: "Test Case 4: Logout User",
    difficulty: "Beginner",
    type: "UI Functional",
    feature: "Logout",
    module: "Authentication",
    goal: "Ensure authenticated user can logout and gets redirected to login page.",
    precondition: "Use valid user credentials to create an authenticated session first.",
    problemStatement: "This scenario validates proper session termination and public-route fallback after logout.",
    executionSteps: [
      "Launch browser.",
      "Navigate to the application home page URL.",
      "Verify that the home page is visible successfully.",
      "Click on the 'Signup / Login' button.",
      "Verify that 'Login to your account' is visible.",
      "Enter correct email address and password.",
      "Click the 'Login' button.",
      "Verify that 'Logged in as username' is visible.",
      "Click the 'Logout' button.",
      "Verify that the user is navigated to the login page.",
    ],
    expectedResults: [
      "Logout action should clear active session.",
      "Login page should be displayed after logout.",
      "Authenticated username should no longer be visible.",
    ],
    locatorHints: ["logout button", "login heading", "logged-in username label"],
    seleniumHint: "Validate back-button behavior if you want to extend this case into session-hardening coverage.",
    playwrightHint: "After logout, assert login heading and optionally reload to confirm session is not restored.",
  }),
  createCase({
    id: "ae-05",
    suite: "Automation Exercise Core",
    title: "Test Case 5: Register User with existing email",
    difficulty: "Beginner",
    type: "Negative UI",
    feature: "Duplicate signup validation",
    module: "Authentication",
    goal: "Ensure duplicate email signup is blocked with correct error messaging.",
    precondition: "A user with the test email should already be registered.",
    problemStatement: "This case protects against duplicate user creation and validates email uniqueness handling.",
    executionSteps: [
      "Launch browser.",
      "Navigate to the application home page URL.",
      "Verify that the home page is visible successfully.",
      "Click on the 'Signup / Login' button.",
      "Verify that 'New User Signup!' is visible.",
      "Enter name and an already registered email address.",
      "Click the 'Signup' button.",
      "Verify that the error 'Email Address already exist!' is visible.",
    ],
    expectedResults: [
      "Existing email should not move to account information screen.",
      "Duplicate email validation should be shown clearly.",
      "No second account should be created with the same email.",
    ],
    locatorHints: ["signup email input", "signup submit button", "duplicate email error"],
    seleniumHint: "Store the existing test email in test data instead of hardcoding it inside the test method.",
    playwrightHint: "Use isolated test data setup so the duplicate user state is deterministic before assertion.",
  }),
  createCase({
    id: "ae-06",
    suite: "Automation Exercise Core",
    title: "Test Case 6: Contact Us Form",
    difficulty: "Intermediate",
    type: "UI + Upload",
    feature: "Contact form with file upload",
    module: "Support",
    goal: "Validate complete contact form submission including file upload and success confirmation.",
    precondition: "Keep a valid test file available on the machine for upload validation.",
    problemStatement: "This scenario covers form input, upload control, alert handling, and post-submit navigation.",
    executionSteps: [
      "Launch browser.",
      "Navigate to the application home page URL.",
      "Verify that the home page is visible successfully.",
      "Click on the 'Contact Us' button.",
      "Verify that 'GET IN TOUCH' is visible.",
      "Enter name, email, subject, and message.",
      "Upload a file using the file input.",
      "Click the 'Submit' button.",
      "Handle the browser alert by clicking OK.",
      "Verify that success message 'Success! Your details have been submitted successfully.' is visible.",
      "Click the 'Home' button and verify that the home page loads successfully.",
    ],
    expectedResults: [
      "Contact form should accept data and file input successfully.",
      "Success message should appear after submission and alert acceptance.",
      "Home navigation should work after form submission.",
    ],
    locatorHints: ["contact us nav action", "name/email/subject/message fields", "file input", "submit button", "success banner"],
    seleniumHint: "Use sendKeys on file input with absolute file path and explicit alert handling after submit.",
    playwrightHint: "Use setInputFiles for upload and page.once('dialog') for alert handling before clicking submit.",
  }),
  createCase({
    id: "ae-07",
    suite: "Automation Exercise Core",
    title: "Test Case 7: Verify Test Cases Page",
    difficulty: "Beginner",
    type: "Navigation",
    feature: "Test Cases page navigation",
    module: "Navigation",
    goal: "Ensure user can open the dedicated Test Cases page from home page navigation.",
    precondition: "Application home page should load successfully.",
    problemStatement: "This case confirms simple navigation integrity to the testcase reference page.",
    executionSteps: [
      "Launch browser.",
      "Navigate to the application home page URL.",
      "Verify that the home page is visible successfully.",
      "Click on the 'Test Cases' button.",
      "Verify that the user is navigated to the test cases page successfully.",
    ],
    expectedResults: [
      "Test Cases page should open from the top navigation.",
      "Page title and testcase content should be visible.",
    ],
    locatorHints: ["test cases nav link", "test cases title heading"],
    seleniumHint: "A short navigation test like this is ideal for smoke suite coverage.",
    playwrightHint: "Assert URL change plus title heading visibility to avoid false positives.",
  }),
  createCase({
    id: "ae-08",
    suite: "Automation Exercise Core",
    title: "Test Case 8: Verify All Products and product detail page",
    difficulty: "Intermediate",
    type: "Catalog",
    feature: "All products and details",
    module: "Catalog",
    goal: "Validate product listing page and the detail screen for a selected product.",
    precondition: "Product data should be available and listing page should render cards.",
    problemStatement: "This scenario ensures the listing page is usable and product detail metadata is visible.",
    executionSteps: [
      "Launch browser.",
      "Navigate to the application home page URL.",
      "Verify that the home page is visible successfully.",
      "Click on the 'Products' button.",
      "Verify that the user is navigated to the ALL PRODUCTS page successfully.",
      "Verify that the products list is visible.",
      "Click on 'View Product' for the first product.",
      "Verify that the user lands on the product detail page.",
      "Verify that product name, category, price, availability, condition, and brand are visible.",
    ],
    expectedResults: [
      "All Products listing page should render product cards.",
      "Selected product detail page should show complete product information.",
    ],
    locatorHints: ["products nav link", "product list grid", "view product action", "product information area"],
    seleniumHint: "Capture product title from listing and compare it on detail page if you want a stronger assertion.",
    playwrightHint: "Use nth-card selection carefully and assert detail section fields after navigation.",
  }),
  createCase({
    id: "ae-09",
    suite: "Automation Exercise Core",
    title: "Test Case 9: Search Product",
    difficulty: "Intermediate",
    type: "Catalog Search",
    feature: "Product search",
    module: "Catalog",
    goal: "Verify keyword search returns related products and correct heading state.",
    precondition: "Choose a product keyword that is known to exist in the catalog.",
    problemStatement: "Search behavior is a high-value catalog journey and should return visible relevant items.",
    executionSteps: [
      "Launch browser.",
      "Navigate to the application home page URL.",
      "Verify that the home page is visible successfully.",
      "Click on the 'Products' button.",
      "Verify that the user is navigated to the ALL PRODUCTS page successfully.",
      "Enter a product name in the search input and click the search button.",
      "Verify that 'SEARCHED PRODUCTS' is visible.",
      "Verify that all visible products are related to the searched keyword.",
    ],
    expectedResults: [
      "Search results section should appear after keyword submission.",
      "Only relevant products should be displayed for the searched keyword.",
    ],
    locatorHints: ["search input", "search submit button", "searched products heading", "result cards"],
    seleniumHint: "Normalize result text to lowercase before comparing with the search keyword for stable assertions.",
    playwrightHint: "Assert result count greater than zero and loop through visible cards for keyword relevance checks.",
  }),
  createCase({
    id: "ae-10",
    suite: "Automation Exercise Core",
    title: "Test Case 10: Verify Subscription in home page",
    difficulty: "Beginner",
    type: "Footer validation",
    feature: "Homepage subscription",
    module: "Subscription",
    goal: "Validate subscription workflow from home page footer.",
    precondition: "Use a syntactically valid email address for subscription testing.",
    problemStatement: "Footer subscription is a simple but common smoke case for marketing capture flows.",
    executionSteps: [
      "Launch browser.",
      "Navigate to the application home page URL.",
      "Verify that the home page is visible successfully.",
      "Scroll down to the footer section.",
      "Verify that 'SUBSCRIPTION' text is visible.",
      "Enter an email address in the subscription input and click the arrow button.",
      "Verify that the success message 'You have been successfully subscribed!' is visible.",
    ],
    expectedResults: [
      "Subscription area should be visible in footer.",
      "Success message should appear after valid email submission.",
    ],
    locatorHints: ["footer subscription heading", "subscription email field", "subscription arrow button"],
    seleniumHint: "Use JavaScript or action-based scrolling if footer is lazy-rendered on some browsers.",
    playwrightHint: "Use locator.scrollIntoViewIfNeeded before asserting footer subscription elements.",
  }),
  createCase({
    id: "ae-11",
    suite: "Automation Exercise Core",
    title: "Test Case 11: Verify Subscription in Cart page",
    difficulty: "Beginner",
    type: "Footer validation",
    feature: "Cart page subscription",
    module: "Subscription",
    goal: "Validate subscription component from the cart page footer area.",
    precondition: "Cart page should be accessible whether cart is empty or populated.",
    problemStatement: "This scenario checks subscription consistency across another page template.",
    executionSteps: [
      "Launch browser.",
      "Navigate to the application home page URL.",
      "Verify that the home page is visible successfully.",
      "Click the 'Cart' button.",
      "Scroll down to the footer section.",
      "Verify that 'SUBSCRIPTION' text is visible.",
      "Enter an email address in the subscription input and click the arrow button.",
      "Verify that the success message 'You have been successfully subscribed!' is visible.",
    ],
    expectedResults: [
      "Subscription area should also work from the cart page.",
      "Success confirmation should appear after email submission.",
    ],
    locatorHints: ["cart nav link", "cart footer subscription block", "success alert"],
    seleniumHint: "This can reuse the same subscription component page object as the home-page subscription case.",
    playwrightHint: "Prefer reusing common footer helpers to keep subscription tests maintainable.",
  }),
  createCase({
    id: "ae-12",
    suite: "Automation Exercise Core",
    title: "Test Case 12: Add Products in Cart",
    difficulty: "Intermediate",
    type: "Cart E2E",
    feature: "Multiple product add to cart",
    module: "Cart",
    goal: "Verify multiple products can be added and displayed with correct price, quantity, and total.",
    precondition: "Products page should contain at least two addable products.",
    problemStatement: "This scenario validates hover-based cart add interactions and multi-item cart verification.",
    executionSteps: [
      "Launch browser.",
      "Navigate to the application home page URL.",
      "Verify that the home page is visible successfully.",
      "Click the 'Products' button.",
      "Hover over the first product and click 'Add to cart'.",
      "Click the 'Continue Shopping' button.",
      "Hover over the second product and click 'Add to cart'.",
      "Click the 'View Cart' button.",
      "Verify that both products are added to the cart.",
      "Verify their prices, quantities, and total prices.",
    ],
    expectedResults: [
      "Two selected products should be present in cart.",
      "Cart should display accurate price, quantity, and total information for each row.",
    ],
    locatorHints: ["product hover area", "add to cart overlay button", "continue shopping modal action", "view cart action", "cart rows"],
    seleniumHint: "Use Actions for hover if add-to-cart is hidden until hover on desktop layout.",
    playwrightHint: "Hover on product card before clicking add-to-cart if CTA visibility depends on hover state.",
  }),
  createCase({
    id: "ae-13",
    suite: "Automation Exercise Core",
    title: "Test Case 13: Verify Product quantity in Cart",
    difficulty: "Intermediate",
    type: "Cart validation",
    feature: "Quantity persistence",
    module: "Cart",
    goal: "Ensure manually increased product quantity is preserved in the cart page.",
    precondition: "Product detail page should contain quantity input and add-to-cart action.",
    problemStatement: "Quantity handling is a classic cart validation area and often breaks when detail-page state is not preserved.",
    executionSteps: [
      "Launch browser.",
      "Navigate to the application home page URL.",
      "Verify that the home page is visible successfully.",
      "Click 'View Product' for any product on the home page.",
      "Verify that the product detail page is opened.",
      "Increase quantity to 4.",
      "Click the 'Add to cart' button.",
      "Click the 'View Cart' button.",
      "Verify that the product is displayed in the cart page with exact quantity 4.",
    ],
    expectedResults: [
      "Chosen quantity from product detail page should persist in cart.",
      "Cart row should reflect the same quantity set before add-to-cart.",
    ],
    locatorHints: ["view product action", "quantity input", "add to cart button", "cart quantity cell"],
    seleniumHint: "Clear quantity field before typing a new value if the control is not a stepper.",
    playwrightHint: "Use fill or press-based control depending on whether the quantity widget is editable or button-driven.",
  }),
  createCase({
    id: "ae-14",
    suite: "Automation Exercise Core",
    title: "Test Case 14: Place Order - Register while Checkout",
    difficulty: "Advanced",
    type: "Checkout E2E",
    feature: "Checkout with registration during flow",
    module: "Checkout",
    goal: "Validate that a guest can begin checkout, register mid-flow, and complete order successfully.",
    precondition: "Use a fresh email and keep valid payment details ready for the order completion step.",
    problemStatement: "This is a critical real-world checkout path where guest users decide to create an account during checkout.",
    executionSteps: [
      "Launch browser.",
      "Navigate to the application home page URL.",
      "Verify that the home page is visible successfully.",
      "Add products to the cart.",
      "Click the 'Cart' button.",
      "Verify that the cart page is displayed.",
      "Click 'Proceed To Checkout'.",
      "Click the 'Register / Login' button.",
      "Fill all signup details and create the account.",
      "Verify that 'ACCOUNT CREATED!' is visible and click the 'Continue' button.",
      "Verify that 'Logged in as username' is visible at the top.",
      "Click the 'Cart' button again.",
      "Click the 'Proceed To Checkout' button.",
      "Verify address details and review your order.",
      "Enter description in the comment text area and click 'Place Order'.",
      "Enter payment details such as name on card, card number, CVC, and expiration date.",
      "Click the 'Pay and Confirm Order' button.",
      "Verify the success message 'Your order has been placed successfully!'",
      "Click the 'Delete Account' button.",
      "Verify that 'ACCOUNT DELETED!' is visible and click the 'Continue' button.",
    ],
    expectedResults: [
      "Checkout should resume successfully after user registration.",
      "Address and order review should be visible before payment.",
      "Order success and account deletion should complete without breaking the flow.",
    ],
    locatorHints: ["proceed to checkout button", "register/login prompt", "comment textarea", "payment form", "order success message"],
    seleniumHint: "Model this as a long end-to-end checkout journey with helper methods for cart, auth, address review, and payment.",
    playwrightHint: "Persist state carefully after registration because the flow returns to cart and checkout after account creation.",
  }),
  createCase({
    id: "ae-15",
    suite: "Automation Exercise Core",
    title: "Test Case 15: Place Order - Register before Checkout",
    difficulty: "Advanced",
    type: "Checkout E2E",
    feature: "Checkout with pre-registered user",
    module: "Checkout",
    goal: "Validate order placement for a user who registers before entering the checkout flow.",
    precondition: "Use a new email and valid payment data for a fresh account-based checkout.",
    problemStatement: "This scenario verifies a smoother customer path where signup happens before cart and checkout activities.",
    executionSteps: [
      "Launch browser.",
      "Navigate to the application home page URL.",
      "Verify that the home page is visible successfully.",
      "Click the 'Signup / Login' button.",
      "Fill all signup details and create the account.",
      "Verify that 'ACCOUNT CREATED!' is visible and click the 'Continue' button.",
      "Verify that 'Logged in as username' is visible at the top.",
      "Add products to the cart.",
      "Click the 'Cart' button.",
      "Verify that the cart page is displayed.",
      "Click 'Proceed To Checkout'.",
      "Verify address details and review your order.",
      "Enter description in the comment text area and click 'Place Order'.",
      "Enter payment details such as name on card, card number, CVC, and expiration date.",
      "Click the 'Pay and Confirm Order' button.",
      "Verify the success message 'Your order has been placed successfully!'",
      "Click the 'Delete Account' button.",
      "Verify that 'ACCOUNT DELETED!' is visible and click the 'Continue' button.",
    ],
    expectedResults: [
      "Newly registered user should be able to place order without registration interruption.",
      "Checkout should show valid address and order review information.",
      "Success confirmation should appear after payment.",
    ],
    locatorHints: ["signup flow elements", "cart page", "proceed to checkout", "payment submit button", "success message"],
    seleniumHint: "Use reusable signup helper plus separate reusable payment helper to keep this long scenario maintainable.",
    playwrightHint: "Tag this as a full e2e regression case because it spans auth, cart, checkout, and post-order verification.",
  }),
  createCase({
    id: "ae-16",
    suite: "Automation Exercise Core",
    title: "Test Case 16: Place Order - Login before Checkout",
    difficulty: "Advanced",
    type: "Checkout E2E",
    feature: "Checkout with existing login",
    module: "Checkout",
    goal: "Validate order placement for an existing registered user who logs in before checkout.",
    precondition: "A valid user account and payment test data should already exist.",
    problemStatement: "This scenario covers the most common repeat-customer checkout flow with existing account login.",
    executionSteps: [
      "Launch browser.",
      "Navigate to the application home page URL.",
      "Verify that the home page is visible successfully.",
      "Click the 'Signup / Login' button.",
      "Enter email and password and click the 'Login' button.",
      "Verify that 'Logged in as username' is visible at the top.",
      "Add products to the cart.",
      "Click the 'Cart' button.",
      "Verify that the cart page is displayed.",
      "Click 'Proceed To Checkout'.",
      "Verify address details and review your order.",
      "Enter description in the comment text area and click 'Place Order'.",
      "Enter payment details such as name on card, card number, CVC, and expiration date.",
      "Click the 'Pay and Confirm Order' button.",
      "Verify the success message 'Your order has been placed successfully!'",
      "Click the 'Delete Account' button.",
      "Verify that 'ACCOUNT DELETED!' is visible and click the 'Continue' button.",
    ],
    expectedResults: [
      "Existing user should be able to complete checkout after login.",
      "Cart, checkout, and payment flow should remain stable for logged-in user.",
      "Order success should be visible after payment confirmation.",
    ],
    locatorHints: ["login fields", "cart navigation", "checkout section", "payment form", "success banner"],
    seleniumHint: "If account deletion is not desired for reusable data, split cleanup strategy by environment.",
    playwrightHint: "You can speed up this scenario later by creating authenticated storage state if needed.",
  }),
  createCase({
    id: "ae-17",
    suite: "Automation Exercise Core",
    title: "Test Case 17: Remove Products From Cart",
    difficulty: "Beginner",
    type: "Cart validation",
    feature: "Cart removal",
    module: "Cart",
    goal: "Verify a product can be removed successfully from the cart page.",
    precondition: "At least one product should already be present in the cart.",
    problemStatement: "This is a basic but important cart maintenance case for quantity and row removal behavior.",
    executionSteps: [
      "Launch browser.",
      "Navigate to the application home page URL.",
      "Verify that the home page is visible successfully.",
      "Add products to the cart.",
      "Click the 'Cart' button.",
      "Verify that the cart page is displayed.",
      "Click the 'X' button corresponding to a particular product.",
      "Verify that the product is removed from the cart.",
    ],
    expectedResults: [
      "Selected product row should disappear after clicking remove action.",
      "Cart should reflect updated state after removal.",
    ],
    locatorHints: ["cart row delete icon", "cart rows container", "empty or reduced cart state"],
    seleniumHint: "Wait for row invisibility instead of only checking click success.",
    playwrightHint: "Use toHaveCount or row-level visibility assertions after removal.",
  }),
  createCase({
    id: "ae-18",
    suite: "Automation Exercise Core",
    title: "Test Case 18: View Category Products",
    difficulty: "Intermediate",
    type: "Catalog navigation",
    feature: "Category browsing",
    module: "Catalog",
    goal: "Validate left-side category navigation and product listing by category/subcategory.",
    precondition: "Sidebar categories should be visible on the home page.",
    problemStatement: "Category navigation is a classic browse flow that validates taxonomy-driven product discovery.",
    executionSteps: [
      "Launch browser.",
      "Navigate to the application home page URL.",
      "Verify that categories are visible on the left sidebar.",
      "Click on the 'Women' category.",
      "Click on any sub-category link under 'Women', for example 'Dress'.",
      "Verify that the category page is displayed and category heading text is visible.",
      "On the left sidebar, click on any sub-category link under the 'Men' category.",
      "Verify that the user is navigated to the selected category page.",
    ],
    expectedResults: [
      "Selecting sidebar category/subcategory should open related product listing.",
      "Category heading should match selected navigation option.",
    ],
    locatorHints: ["category sidebar", "women category expander", "subcategory link", "category page heading"],
    seleniumHint: "If the category tree is collapsible, wait for subcategory visibility before clicking deeper links.",
    playwrightHint: "Use text-based locators for category tree navigation and assert heading text after each selection.",
  }),
  createCase({
    id: "ae-19",
    suite: "Automation Exercise Core",
    title: "Test Case 19: View & Cart Brand Products",
    difficulty: "Intermediate",
    type: "Catalog navigation",
    feature: "Brand filtering",
    module: "Catalog",
    goal: "Validate brand-level product navigation using sidebar brand filters.",
    precondition: "Brand section must be visible on the product listing page.",
    problemStatement: "Brand browsing validates alternative discovery flow beyond categories and search.",
    executionSteps: [
      "Launch browser.",
      "Navigate to the application home page URL.",
      "Click on the 'Products' button.",
      "Verify that Brands are visible on the left side bar.",
      "Click on any brand name.",
      "Verify that the user is navigated to the brand page and brand products are displayed.",
      "On the left side bar, click on another brand link.",
      "Verify that the user is navigated to that brand page and can see products.",
    ],
    expectedResults: [
      "Brand filter selection should open brand-specific product listing.",
      "Switching brands should update listing correctly.",
    ],
    locatorHints: ["products nav link", "brand sidebar", "brand heading", "brand product cards"],
    seleniumHint: "Capture selected brand text before clicking and compare it with resulting page heading.",
    playwrightHint: "Use brand sidebar text locators and assert both heading and product presence after navigation.",
  }),
  createCase({
    id: "ae-20",
    suite: "Automation Exercise Core",
    title: "Test Case 20: Search Products and Verify Cart After Login",
    difficulty: "Advanced",
    type: "Cross-state cart validation",
    feature: "Search + cart persistence after login",
    module: "Catalog and Cart",
    goal: "Ensure products searched and added before login remain visible in cart after login.",
    precondition: "A valid user account should exist for login after pre-login cart operations.",
    problemStatement: "This scenario validates cart persistence across authentication boundary, which is a real-world ecommerce risk area.",
    executionSteps: [
      "Launch browser.",
      "Navigate to the application home page URL.",
      "Click on the 'Products' button.",
      "Verify that the user is navigated to the ALL PRODUCTS page successfully.",
      "Enter product name in the search input and click the search button.",
      "Verify that 'SEARCHED PRODUCTS' is visible.",
      "Verify that all products related to the search are visible.",
      "Add those products to the cart.",
      "Click the 'Cart' button and verify that products are visible in the cart.",
      "Click the 'Signup / Login' button and submit valid login details.",
      "Go to the Cart page again.",
      "Verify that the previously added products are still visible in the cart after login.",
    ],
    expectedResults: [
      "Products added before login should persist after user logs in.",
      "Cart content should remain unchanged across authentication transition.",
    ],
    locatorHints: ["search input", "search results section", "add to cart actions", "cart page", "login form"],
    seleniumHint: "This is a great state-persistence test; store item names before login and compare them after login.",
    playwrightHint: "Use arrays of item titles before and after login and compare with deep equality assertions.",
  }),
  createCase({
    id: "ae-21",
    suite: "Automation Exercise Core",
    title: "Test Case 21: Add review on product",
    difficulty: "Intermediate",
    type: "Form submission",
    feature: "Product review",
    module: "Catalog Detail",
    goal: "Validate review form visibility and successful review submission on product detail page.",
    precondition: "A product detail page with review section should be reachable.",
    problemStatement: "Review submission combines product detail navigation with form submission and success feedback.",
    executionSteps: [
      "Launch browser.",
      "Navigate to the application home page URL.",
      "Click on the 'Products' button.",
      "Verify that the user is navigated to the ALL PRODUCTS page successfully.",
      "Click the 'View Product' button.",
      "Verify that 'Write Your Review' is visible.",
      "Enter name, email, and review text.",
      "Click the 'Submit' button.",
      "Verify the success message 'Thank you for your review.'",
    ],
    expectedResults: [
      "Review form should be visible on product detail page.",
      "Success confirmation should appear after review submission.",
    ],
    locatorHints: ["view product button", "write your review heading", "review name/email/textarea", "submit review button"],
    seleniumHint: "A short wait for toast or success alert is important because review confirmation may appear asynchronously.",
    playwrightHint: "Use expect on the thank-you message after clicking submit rather than relying only on no-error submission.",
  }),
  createCase({
    id: "ae-22",
    suite: "Automation Exercise Core",
    title: "Test Case 22: Add to cart from Recommended items",
    difficulty: "Intermediate",
    type: "Cart E2E",
    feature: "Recommended items add to cart",
    module: "Recommendations",
    goal: "Validate recommended item section and adding a recommended product to cart.",
    precondition: "Recommended items section should be visible near the bottom of home page.",
    problemStatement: "This case verifies recommendation carousel or section behavior plus downstream cart add flow.",
    executionSteps: [
      "Launch browser.",
      "Navigate to the application home page URL.",
      "Scroll to the bottom of the page.",
      "Verify that 'RECOMMENDED ITEMS' are visible.",
      "Click on 'Add To Cart' for a recommended product.",
      "Click the 'View Cart' button.",
      "Verify that the recommended product is displayed on the cart page.",
    ],
    expectedResults: [
      "Recommended items section should be visible at page bottom.",
      "Selected recommended item should be added to cart successfully.",
    ],
    locatorHints: ["recommended items section", "recommended add to cart button", "view cart action", "cart product rows"],
    seleniumHint: "Scroll handling matters here because recommendation sections are often below the fold or lazy-rendered.",
    playwrightHint: "Scroll into the recommendation container before interacting with nested CTA buttons.",
  }),
  createCase({
    id: "ae-23",
    suite: "Automation Exercise Core",
    title: "Test Case 23: Verify address details in checkout page",
    difficulty: "Advanced",
    type: "Checkout validation",
    feature: "Delivery and billing address verification",
    module: "Checkout",
    goal: "Ensure checkout page shows the same delivery and billing address used during registration.",
    precondition: "Create a fresh account with complete address data before entering checkout.",
    problemStatement: "Address fidelity between registration and checkout is critical for trustworthy order processing.",
    executionSteps: [
      "Launch browser.",
      "Navigate to the application home page URL.",
      "Verify that the home page is visible successfully.",
      "Click the 'Signup / Login' button.",
      "Fill all signup details and create the account.",
      "Verify that 'ACCOUNT CREATED!' is visible and click the 'Continue' button.",
      "Verify that 'Logged in as username' is visible at the top.",
      "Add products to the cart.",
      "Click the 'Cart' button.",
      "Verify that the cart page is displayed.",
      "Click 'Proceed To Checkout'.",
      "Verify that the delivery address is the same address entered during registration.",
      "Verify that the billing address is the same address entered during registration.",
      "Click the 'Delete Account' button.",
      "Verify that 'ACCOUNT DELETED!' is visible and click the 'Continue' button.",
    ],
    expectedResults: [
      "Delivery and billing addresses should match the registered user profile address.",
      "No mismatch should appear between registration and checkout address blocks.",
    ],
    locatorHints: ["checkout address blocks", "delivery address section", "billing address section", "registered account data reference"],
    seleniumHint: "Store address data at signup stage and compare line-by-line on checkout rather than only checking visibility.",
    playwrightHint: "You can assert full address block text using contains checks for city, state, zipcode, and phone number.",
  }),
  createCase({
    id: "ae-24",
    suite: "Automation Exercise Core",
    title: "Test Case 24: Download Invoice after purchase order",
    difficulty: "Advanced",
    type: "Checkout + file handling",
    feature: "Invoice download",
    module: "Post-order",
    goal: "Validate invoice download behavior after successful order placement.",
    precondition: "A guest-to-registered checkout flow should be completed successfully with downloadable invoice option visible.",
    problemStatement: "Invoice generation and download is a critical post-purchase artifact and common automation challenge.",
    executionSteps: [
      "Launch browser.",
      "Navigate to the application home page URL.",
      "Verify that the home page is visible successfully.",
      "Add products to the cart.",
      "Click the 'Cart' button.",
      "Verify that the cart page is displayed.",
      "Click 'Proceed To Checkout'.",
      "Click the 'Register / Login' button.",
      "Fill all signup details and create the account.",
      "Verify that 'ACCOUNT CREATED!' is visible and click the 'Continue' button.",
      "Verify that 'Logged in as username' is visible at the top.",
      "Click the 'Cart' button.",
      "Click the 'Proceed To Checkout' button.",
      "Verify address details and review your order.",
      "Enter description in the comment text area and click 'Place Order'.",
      "Enter payment details such as name on card, card number, CVC, and expiration date.",
      "Click the 'Pay and Confirm Order' button.",
      "Verify the success message 'Your order has been placed successfully!'",
      "Click the 'Download Invoice' button and verify that invoice is downloaded successfully.",
      "Click the 'Continue' button.",
      "Click the 'Delete Account' button.",
      "Verify that 'ACCOUNT DELETED!' is visible and click the 'Continue' button.",
    ],
    expectedResults: [
      "Order should complete successfully and invoice download option should appear.",
      "Invoice file should be downloaded after clicking download action.",
      "Post-download flow should continue successfully.",
    ],
    locatorHints: ["download invoice button", "browser download state", "success page actions", "delete account action"],
    seleniumHint: "Use browser download directory configuration and file existence assertions for real invoice verification.",
    playwrightHint: "Use page.waitForEvent('download') and assert suggested filename or saved file path.",
  }),
  createCase({
    id: "ae-25",
    suite: "Automation Exercise Core",
    title: "Test Case 25: Verify Scroll Up using Arrow button and Scroll Down functionality",
    difficulty: "Beginner",
    type: "UX behavior",
    feature: "Scroll up via arrow button",
    module: "Page Behavior",
    goal: "Validate footer scroll-down state and upward scroll behavior using floating arrow button.",
    precondition: "Home page should contain footer subscription section and bottom-right scroll-up control.",
    problemStatement: "This case validates scroll utilities that are often missed in automation but matter in UX regression.",
    executionSteps: [
      "Launch browser.",
      "Navigate to the application home page URL.",
      "Verify that the home page is visible successfully.",
      "Scroll down the page to the bottom.",
      "Verify that 'SUBSCRIPTION' is visible.",
      "Click on the arrow at the bottom right side to move upward.",
      "Verify that the page is scrolled up and the homepage hero text is visible on screen.",
    ],
    expectedResults: [
      "Scroll-up arrow should be visible after moving to bottom area.",
      "Clicking the arrow should move the page back upward successfully.",
    ],
    locatorHints: ["footer subscription text", "scroll up arrow button", "hero headline text"],
    seleniumHint: "Read current scroll position before and after clicking the arrow to create a stronger assertion.",
    playwrightHint: "Combine text visibility at footer and top hero with page.evaluate scroll position checks if needed.",
  }),
  createCase({
    id: "ae-26",
    suite: "Automation Exercise Core",
    title: "Test Case 26: Verify Scroll Up without Arrow button and Scroll Down functionality",
    difficulty: "Beginner",
    type: "UX behavior",
    feature: "Manual scroll to top",
    module: "Page Behavior",
    goal: "Validate that manual upward scroll returns user to the top hero area successfully.",
    precondition: "Home page should be scrollable from hero to footer region.",
    problemStatement: "This scenario complements arrow-button behavior by validating standard manual scroll behavior and content visibility.",
    executionSteps: [
      "Launch browser.",
      "Navigate to the application home page URL.",
      "Verify that the home page is visible successfully.",
      "Scroll down the page to the bottom.",
      "Verify that 'SUBSCRIPTION' is visible.",
      "Scroll up the page manually to the top.",
      "Verify that the page is scrolled up and the homepage hero text is visible on screen.",
    ],
    expectedResults: [
      "Footer subscription area should be reachable by scrolling down.",
      "Manual scroll back to top should make hero banner text visible again.",
    ],
    locatorHints: ["footer subscription heading", "browser scroll position", "hero text block"],
    seleniumHint: "Use JavaScript scrollTo for deterministic top scroll assertion if wheel action is flaky in CI.",
    playwrightHint: "Use window.scrollTo and then assert top hero content visibility for stable automation.",
  }),
];
